import React, {
  createContext, useContext, useEffect,
  useMemo, useRef, useState,
} from "react";
import Swal from "sweetalert2";
import {
  socket,
  registerUser,
  callUser       as socketCallUser,
  acceptCall     as socketAcceptCall,
  rejectCall     as socketRejectCall,
  endCall        as socketEndCall,
  sendWebrtcOffer,
  sendWebrtcAnswer,
  sendIceCandidate,
} from "../services/socket";

// ─── License helpers ──────────────────────────────────────────────────────────
const isCallChatAllowed = () => {
  try {
    return JSON.parse(localStorage.getItem('license_call_chat') || '{"allowed":true}').allowed !== false;
  } catch { return true; }
};

const showCallChatBlocked = () => {
  const reason = (() => {
    try { return JSON.parse(localStorage.getItem('license_call_chat') || '{}').reason || ''; } catch { return ''; }
  })();
  Swal.fire({
    icon: 'error',
    title: '🔒 Call & Chat License Expired',
    html: `<p style="color:#64748b;font-size:14px">${reason || 'Your Call & Chat license has expired. Contact TotalCare support.'}</p>`,
    confirmButtonText: 'OK',
    confirmButtonColor: '#d33',
  });
};
// ─────────────────────────────────────────────────────────────────────────────

const CallContext = createContext(null);
export const useCall = () => useContext(CallContext);

export const CallProvider = ({ user, children }) => {
  const [onlineUsers, setOnlineUsers] = useState([]);
  const [isWidgetOpen, setIsWidgetOpen] = useState(false);
  const [incomingCall, setIncomingCall] = useState(null);
  const [currentCall, setCurrentCall]   = useState(null);
  const [callStatus, setCallStatus]     = useState("idle");

  const remoteAudioRef      = useRef(null);
  const ringtoneRef         = useRef(null);
  const peerRef             = useRef(null);
  const localStreamRef      = useRef(null);
  const currentCallRef      = useRef(null);
  const pendingIncomingRef  = useRef(null);
  const acceptedIncomingRef = useRef(null);
  const callTimeoutRef      = useRef(null);
  const iceCandidateQueue   = useRef([]);
  const audioUnlockedRef    = useRef(false);

  useEffect(() => { currentCallRef.current = currentCall; },   [currentCall]);
  useEffect(() => { pendingIncomingRef.current = incomingCall; }, [incomingCall]);

  useEffect(() => {
    const unlock = async () => {
      if (audioUnlockedRef.current) return;
      audioUnlockedRef.current = true;
      try {
        const AudioCtx = window.AudioContext || window.webkitAudioContext;
        if (AudioCtx) {
          const ctx = new AudioCtx();
          await ctx.resume();
          ctx.close();
        }
        if (ringtoneRef.current) {
          await ringtoneRef.current.play();
          ringtoneRef.current.pause();
          ringtoneRef.current.currentTime = 0;
        }
      } catch (_) { /* silent */ }
    };

    document.addEventListener("click",      unlock, { once: true });
    document.addEventListener("touchstart", unlock, { once: true });
    document.addEventListener("keydown",    unlock, { once: true });

    return () => {
      document.removeEventListener("click",      unlock);
      document.removeEventListener("touchstart", unlock);
      document.removeEventListener("keydown",    unlock);
    };
  }, []);

  const clearCallTimeout = () => {
    if (callTimeoutRef.current) {
      clearTimeout(callTimeoutRef.current);
      callTimeoutRef.current = null;
    }
  };

  const playRingtone = async () => {
    try {
      if (!ringtoneRef.current) return;
      ringtoneRef.current.currentTime = 0;
      ringtoneRef.current.loop = true;
      await ringtoneRef.current.play();
    } catch (err) { console.warn("Ringtone play failed:", err); }
  };

  const stopRingtone = () => {
    try {
      if (!ringtoneRef.current) return;
      ringtoneRef.current.pause();
      ringtoneRef.current.currentTime = 0;
    } catch (err) { console.warn("Ringtone stop failed:", err); }
  };

  const getLocalStream = async () => {
    if (localStreamRef.current) return localStreamRef.current;

    const stream = await navigator.mediaDevices.getUserMedia({
      audio: {
        echoCancellation: { ideal: true },
        noiseSuppression: { ideal: true },
        autoGainControl:  { ideal: true },
        sampleRate:       { ideal: 48000 },
        channelCount:     { ideal: 1 },
      },
    });

    const audioTrack = stream.getAudioTracks()[0];
    if (audioTrack) {
      audioTrack.applyConstraints({
        googEchoCancellation:  true,
        googEchoCancellation2: true,
        googAutoGainControl:   true,
        googNoiseSuppression:  true,
        googNoiseSuppression2: true,
        googHighpassFilter:    true,
      }).catch(() => {});
    }

    localStreamRef.current = stream;
    return stream;
  };

  const ensurePeerConnection = async (targetUserId, callId) => {
    if (peerRef.current) return peerRef.current;

    const pc = new RTCPeerConnection({
      iceServers: [
        { urls: "stun:stun.l.google.com:19302" },
        { urls: "stun:stun1.l.google.com:19302" },
      ],
      iceTransportPolicy: "all",
      bundlePolicy:       "max-bundle",
      rtcpMuxPolicy:      "require",
    });

    pc.onicecandidate = (event) => {
      if (event.candidate) sendIceCandidate(callId, event.candidate, targetUserId);
    };

    pc.ontrack = (event) => {
      if (!remoteAudioRef.current) return;
      const audio = remoteAudioRef.current;
      audio.srcObject = null;
      audio.srcObject = event.streams[0];
      audio.muted     = false;
      audio.volume    = 1.0;
      audio.play().catch(console.error);
    };

    pc.oniceconnectionstatechange = () => {
      console.log("ICE state:", pc.iceConnectionState);
      if (pc.iceConnectionState === "failed") pc.restartIce();
    };

    pc.onconnectionstatechange = () => {
      console.log("Peer state:", pc.connectionState);
    };

    const stream = await getLocalStream();
    stream.getTracks().forEach((track) => pc.addTrack(track, stream));

    const setOpusPreference = (sdp) => {
      const lines    = sdp.split("\r\n");
      const mIdx     = lines.findIndex((l) => l.startsWith("m=audio"));
      if (mIdx === -1) return sdp;
      const opusLine = lines.find((l) => l.toLowerCase().includes("opus/48000"));
      if (!opusLine)   return sdp;
      const payload  = opusLine.match(/(\d+) opus/i)?.[1];
      if (!payload)    return sdp;

      const mParts  = lines[mIdx].split(" ").filter((p) => p !== payload);
      mParts.splice(3, 0, payload);
      lines[mIdx]   = mParts.join(" ");

      const fmtpIdx = lines.findIndex((l) => l.includes(`a=fmtp:${payload}`));
      const fmtp    = `a=fmtp:${payload} minptime=10;useinbandfec=1;stereo=0;maxaveragebitrate=40000;usedtx=1`;
      if (fmtpIdx !== -1) lines[fmtpIdx] = fmtp;
      else lines.splice(mIdx + 1, 0, fmtp);

      return lines.join("\r\n");
    };

    const origOffer  = pc.createOffer.bind(pc);
    const origAnswer = pc.createAnswer.bind(pc);
    pc.createOffer   = async (o) => { const r = await origOffer(o);  return { ...r, sdp: setOpusPreference(r.sdp) }; };
    pc.createAnswer  = async (o) => { const r = await origAnswer(o); return { ...r, sdp: setOpusPreference(r.sdp) }; };

    peerRef.current = pc;
    return pc;
  };

  useEffect(() => {
    if (!user?.trans_code) return;

    if (!socket.connected) socket.connect();

    const onConnect = () => {
      console.log("SOCKET CONNECTED:", socket.id);
      registerUser(String(user.trans_code));
    };

    const onOnlineUsers = (users) => {
      const filtered = Array.isArray(users)
        ? users.filter((u) => String(u.id) !== String(user.trans_code))
        : [];
      setOnlineUsers(filtered);
    };

    const onPresenceUpdated = ({ userId, status }) => {
      setOnlineUsers((prev) =>
        prev.map((u) =>
          String(u.id) === String(userId)
            ? { ...u, online: status !== "offline", callStatus: status }
            : u
        )
      );
    };

    const onIncomingCall = async (payload) => {
      setIncomingCall(payload);
      setCallStatus("ringing");
      await playRingtone();
    };

    const onOutgoingCall = (payload) => {
      setCurrentCall(payload);
      setCallStatus("calling");
    };

    const onCallAccepted = async ({ callId }) => {
      clearCallTimeout();
      stopRingtone();
      const active = currentCallRef.current;
      setCallStatus("connected");
      if (active?.calleeId) {
        await createOffer(callId, active.calleeId);
        setCurrentCall((prev) => ({ ...prev, callId }));
      }
    };

    const onCallRejected = () => {
      clearCallTimeout();
      stopRingtone();
      cleanupCall();
    };

    const onCallEnded = () => {
      clearCallTimeout();
      stopRingtone();
      cleanupCall();
    };

    const onCallError = (payload) => {
      clearCallTimeout();
      stopRingtone();
      alert(payload?.message || "Call failed");
      cleanupCall();
    };

    const onWebrtcOffer = async ({ callId, offer }) => {
      const callerId =
        pendingIncomingRef.current?.callerId  ||
        acceptedIncomingRef.current?.callerId ||
        currentCallRef.current?.callerId;

      if (!callerId) return console.error("No callerId for WebRTC offer");

      try {
        const pc = await ensurePeerConnection(callerId, callId);
        await pc.setRemoteDescription(new RTCSessionDescription(offer));

        while (iceCandidateQueue.current.length) {
          await pc.addIceCandidate(new RTCIceCandidate(iceCandidateQueue.current.shift()));
        }

        const answer = await pc.createAnswer();
        await pc.setLocalDescription(answer);
        sendWebrtcAnswer(callId, answer, callerId);
      } catch (err) { console.error("webrtc-offer error:", err); }
    };

    const onWebrtcAnswer = async ({ answer }) => {
      try {
        if (!peerRef.current) return;
        await peerRef.current.setRemoteDescription(new RTCSessionDescription(answer));
        while (iceCandidateQueue.current.length) {
          await peerRef.current.addIceCandidate(new RTCIceCandidate(iceCandidateQueue.current.shift()));
        }
      } catch (err) { console.error("webrtc-answer error:", err); }
    };

    const onIceCandidate = async ({ candidate }) => {
      if (!candidate) return;
      try {
        if (peerRef.current?.remoteDescription) {
          await peerRef.current.addIceCandidate(new RTCIceCandidate(candidate));
        } else {
          iceCandidateQueue.current.push(candidate);
        }
      } catch (err) { console.error("ICE candidate error:", err); }
    };

    socket.on("connect",          onConnect);
    socket.on("online-users",     onOnlineUsers);
    socket.on("presence-updated", onPresenceUpdated);
    socket.on("incoming-call",    onIncomingCall);
    socket.on("outgoing-call",    onOutgoingCall);
    socket.on("call-accepted",    onCallAccepted);
    socket.on("call-rejected",    onCallRejected);
    socket.on("call-ended",       onCallEnded);
    socket.on("call-error",       onCallError);
    socket.on("webrtc-offer",     onWebrtcOffer);
    socket.on("webrtc-answer",    onWebrtcAnswer);
    socket.on("ice-candidate",    onIceCandidate);

    if (socket.connected) onConnect();

    return () => {
      stopRingtone();
      socket.off("connect",          onConnect);
      socket.off("online-users",     onOnlineUsers);
      socket.off("presence-updated", onPresenceUpdated);
      socket.off("incoming-call",    onIncomingCall);
      socket.off("outgoing-call",    onOutgoingCall);
      socket.off("call-accepted",    onCallAccepted);
      socket.off("call-rejected",    onCallRejected);
      socket.off("call-ended",       onCallEnded);
      socket.off("call-error",       onCallError);
      socket.off("webrtc-offer",     onWebrtcOffer);
      socket.off("webrtc-answer",    onWebrtcAnswer);
      socket.off("ice-candidate",    onIceCandidate);
    };
  }, [user]);

  const createOffer = async (callId, calleeId) => {
    try {
      const pc    = await ensurePeerConnection(calleeId, callId);
      const offer = await pc.createOffer();
      await pc.setLocalDescription(offer);
      sendWebrtcOffer(callId, offer, calleeId);

      pc.getSenders().forEach((sender) => {
        if (sender.track?.kind === "audio") {
          sender.track.applyConstraints({
            echoCancellation: true,
            noiseSuppression: true,
            autoGainControl:  true,
          }).catch(console.warn);
        }
      });
    } catch (err) { console.error("createOffer error:", err); }
  };

  const startCall = (targetUser) => {
    // ── LICENSE CHECK ─────────────────────────────────────────────────────
    if (!isCallChatAllowed()) { showCallChatBlocked(); return; }
    // ─────────────────────────────────────────────────────────────────────

    if (!targetUser?.id) return;

    socketCallUser(String(user.trans_code), String(targetUser.id));

    setCurrentCall({
      callId:           null,
      calleeId:         targetUser.id,
      calleeName:       targetUser.name,
      calleeDepartment: targetUser.department,
    });

    setCallStatus("calling");

    callTimeoutRef.current = setTimeout(() => {
      const callId = currentCallRef.current?.callId;
      if (callId) socketEndCall(callId);
      cleanupCall();
    }, 45000);
  };

  const acceptCall = async () => {
    // ── LICENSE CHECK ─────────────────────────────────────────────────────
    if (!isCallChatAllowed()) {
      showCallChatBlocked();
      stopRingtone();
      socketRejectCall(incomingCall.callId);
      setIncomingCall(null);
      setCallStatus("idle");
      return;
    }
    // ─────────────────────────────────────────────────────────────────────

    if (!incomingCall?.callId) return;
    try {
      const acceptedCall = incomingCall;
      acceptedIncomingRef.current = acceptedCall;
      stopRingtone();
      await getLocalStream();
      socketAcceptCall(acceptedCall.callId);
      setCurrentCall(acceptedCall);
      setIncomingCall(null);
      setCallStatus("connected");
    } catch (err) {
      console.error("acceptCall error:", err);
      alert("Microphone permission is required for calls.");
    }
  };

  const rejectCall = () => {
    if (!incomingCall?.callId) return;
    stopRingtone();
    socketRejectCall(incomingCall.callId);
    setIncomingCall(null);
    setCallStatus("idle");
  };

  const endCall = () => {
    clearCallTimeout();
    stopRingtone();
    const callId = currentCall?.callId || incomingCall?.callId;
    if (callId) socketEndCall(callId);
    cleanupCall();
  };

  const cleanupCall = () => {
    clearCallTimeout();
    stopRingtone();
    iceCandidateQueue.current   = [];
    acceptedIncomingRef.current = null;

    if (peerRef.current) {
      peerRef.current.close();
      peerRef.current = null;
    }
    if (localStreamRef.current) {
      localStreamRef.current.getTracks().forEach((t) => t.stop());
      localStreamRef.current = null;
    }
    if (remoteAudioRef.current) {
      remoteAudioRef.current.srcObject = null;
    }

    setIncomingCall(null);
    setCurrentCall(null);
    setCallStatus("idle");
  };

  const value = useMemo(() => ({
    onlineUsers,
    isWidgetOpen,
    setIsWidgetOpen,
    incomingCall,
    currentCall,
    callStatus,
    remoteAudioRef,
    startCall,
    acceptCall,
    rejectCall,
    endCall,
  }), [onlineUsers, isWidgetOpen, incomingCall, currentCall, callStatus]);

  return (
    <CallContext.Provider value={value}>
      {children}
      <audio ref={remoteAudioRef} playsInline />
      <audio ref={ringtoneRef} src="/ringtone.mp3" preload="auto" />
    </CallContext.Provider>
  );
};